import { create } from 'zustand';
import { TASK_AUTO_ARCHIVE_DAYS, TASK_BOARD_DONE_LIMIT, TASK_BOARD_DONE_MAX, type TaskBoard, type TaskFinishedPage, type TaskCard, type TaskContextRef, type TaskLiveMessage, type TaskMember, type TaskTagCount, type PrMergeBatch } from '@shared/types/tasks';
import { compareRank } from '@shared/task-rank';
import { setOptimisticRefetch } from './taskOptimistic';
import { pageContextPath, webPageContextId } from '@shared/task-contexts';
import { toast } from 'sonner';
import { getFinishedTasks, getTaskBoard, getTaskCards, scopeParam } from '@/lib/tasks-api';
import { getCurrentProject, onProjectChange } from '@/lib/current-project';
import { rememberLiveMeta, rememberTaskMeta } from '@/lib/task-meta';

/**
 * Task data shared by the Board, Tasks panels and row badges, plus the live-update ticks the
 * socket bumps. Boards are cached per scope so a page's panel and grid share one fetch.
 * A change to one task refetches only its card into every loaded board; the whole
 * board is fetched again only for board-wide changes (columns, labels, types) and reconnects.
 * The Board has only the latest finished tasks: Show older fetches it again with more, and its
 * search adds the older ones it finds (looked for again after each full fetch).
 */

export interface BoardQuery {
  scope?: TaskContextRef[];
  archived?: boolean;
  /** T-71: orphaned sub-tasks too, those off the Board included (the Board's Orphaned sub-tasks filter). */
  orphaned?: boolean;
}

export function boardKey(query: BoardQuery): string {
  return `${query.scope?.length ? scopeParam(query.scope) : 'all'}|${query.archived ? 'archived' : 'open'}${query.orphaned ? '|orphaned' : ''}`;
}

interface BoardEntry {
  query: BoardQuery;
  data: TaskBoard | null;
  /** When the request behind `data` started; optimistic changes confirmed later still show. */
  fetchedFrom: number;
  /** Cards refetched on their own since: task id → when that request started. */
  cardsFetchedFrom: Record<string, number>;
  /** How many finished tasks to fetch (the Board; Show older raises it). */
  doneLimit: number;
  /** Show older's fetch is on its way. */
  loadingOlder: boolean;
  /**
   * Older finished cards the search added (not the board's own), the text they were found for,
   * whether more matched than were sent, and the latest request (an earlier one's answer is dropped).
   */
  search: { ids: string[]; text: string | null; error: string | null; more: boolean; seq: number };
  loading: boolean;
  error: string | null;
}

/** The floating Tasks panel: open, narrowed to one row's Context, or showing one task. */
export interface TaskPanelState {
  open: boolean;
  focus: (TaskContextRef & { title: string }) | null;
  taskId: string | null;
  /** Open the New task form for the page's Context (e.g. "Create task" on an email). */
  create?: boolean;
  /**
   * With `create`: fill the New task form in, and link it to this Context rather than the page's
   * (the page may have moved on, e.g. to the next email after Done).
   */
  draft?: { title: string; text?: string; context?: TaskContextRef & { title: string } } | null;
}

interface TaskState {
  boards: Record<string, BoardEntry>;
  panel: TaskPanelState;
  setPanel: (panel: Partial<TaskPanelState>) => void;
  /** Bumped by board-wide live changes and reconnects; hooks refetch the whole board when it changes. */
  boardTick: number;
  taskTicks: Record<string, number>;
  /**
   * Bumped when GitHub says a task's pull requests changed (`task_prs`, T-13) and, for every task, on
   * a reconnect (`prsTick`): the card reads their live state again.
   */
  prTicks: Record<string, number>;
  prsTick: number;
  /** SF-T-202: GitHub said the repo's PRs or Actions runs changed (or a reconnect): the Board header reads them again. */
  githubTick: number;
  /**
   * SF-T-231: a task's dev servers moved on (`dev_servers`: a Start, Stop or Tidy's step or answer, or a report
   * while one settles): that task's panel (`devServersTicks`) and the Board's dialog (`devServersTick`, any task's,
   * and every reconnect) read them again.
   */
  devServersTicks: Record<string, number>;
  devServersTick: number;
  /**
   * Bumped on every reconnect that may have missed changes, for hooks following one task that may not
   * have a `taskTicks` entry yet (SF-T-153).
   */
  resyncTick: number;
  /** SF-T-198: Merge PRs batches this person started, by id, as they last heard (`pr_merge_batch`, or read back). */
  prMergeBatches: Record<string, PrMergeBatch>;
  setPrMergeBatch: (batch: PrMergeBatch) => void;
  notificationTick: number;
  connected: boolean;
  /** When the live socket went down and has stayed down (epoch ms); null while connected or idle. */
  disconnectedAt: number | null;
  loadBoard: (query: BoardQuery) => Promise<void>;
  /** The next page of finished tasks into this board (its Done's Show older). */
  loadOlderDone: (query: BoardQuery) => Promise<void>;
  /** Finished tasks the board didn't load that match its search. */
  searchFinished: (query: BoardQuery, q: string) => Promise<void>;
  receive: (message: TaskLiveMessage) => void;
  resync: () => void;
  /**
   * `idle`: no socket is wanted (the page is closing, or the person can't use it), which is not an
   * outage.
   */
  setConnected: (connected: boolean, options?: { idle?: boolean }) => void;
}

/**
 * SF-T-198: how far a Merge PRs batch has got (finished, then PRs settled, then PRs merged, then tries under
 * way), to keep the newer of two copies. Merged counts apart: a PR skipped by a stop can still turn out merged
 * (it was merging when the stop came), never the other way, so a stop's answer arriving after that news loses.
 */
export function batchProgress(batch: PrMergeBatch): number {
  const count = (state: string) => batch.items.filter((i) => i.state === state).length;
  return (batch.finished_at ? 1_000_000 : 0) + (count('merged') + count('skipped')) * 1000 + count('merged') * 10 + count('waiting');
}

const inFlight = new Map<string, { request: Promise<void>; tick: number; startedAt: number; done: number }>();

/** Wait after a task changes before refetching its card, so a burst of changes is one request. */
const CARD_REFRESH_MS = 250;
/** Most cards per request (the Worker's CARDS_MAX_IDS). */
const CARDS_PER_REQUEST = 100;
/** How long a refetched card is kept for boards whose fetch started before it (longer while one is still on its way). */
const FRESH_CARD_MS = 30_000;

/** A card as fetched on its own: null when the task is gone. */
interface CardUpdate {
  id: string;
  card: TaskCard | null;
  /** When its request started. */
  at: number;
}

/** Tasks that changed since the last card refetch. */
const staleTaskIds = new Set<string>();
let cardTimer: ReturnType<typeof setTimeout> | null = null;
/** Recently refetched cards, re-applied to a board whose fetch started before them but landed after. */
const freshCards = new Map<string, CardUpdate>();
/** The newest tag counts and members from a card refetch, and when its request started. */
interface BoardMeta {
  tags: TaskTagCount[];
  members: TaskMember[];
  at: number;
}
let freshMeta: BoardMeta | null = null;

export function cardInScope(card: TaskCard, scope: TaskContextRef[] | undefined): boolean {
  if (!scope?.length) return true;
  return scope.some((ref) =>
    card.contexts.some((c) =>
      // As the Worker's scopeCondition: pages and web pages match on their path without the query.
      ref.type === 'page' || ref.type === 'url'
        ? c.type === ref.type && pageContextPath(c.id) === pageContextPath(ref.type === 'url' ? (webPageContextId(ref.id) ?? ref.id) : ref.id)
        : (c.type === ref.type && c.id === ref.id) || (c.parent?.type === ref.type && c.parent.id === ref.id)
    )
  );
}

/** T-42: a sub-task is off the Board, as the Worker's ON_BOARD_SQL, unless it's shown there too. */
export function isOnBoard(card: Pick<TaskCard, 'parent' | 'show_on_board'>): boolean {
  return !card.parent || card.show_on_board;
}

/**
 * Whether this board shows the card: in its scope, on the Board (T-42) or an orphaned sub-task on a
 * board that asked for them (T-71), and not archived unless it shows archived.
 */
function belongsOn(query: BoardQuery, card: TaskCard): boolean {
  const onBoard = isOnBoard(card) || (Boolean(query.orphaned) && Boolean(card.parent?.orphaned));
  return cardInScope(card, query.scope) && onBoard && (Boolean(query.archived) || !card.archived);
}

function sortCards(tasks: TaskCard[], columns: TaskBoard['columns']): TaskCard[] {
  const order = columns.map((c) => c.id);
  return tasks.sort((a, b) => order.indexOf(a.column_id) - order.indexOf(b.column_id) || compareRank(a.rank, b.rank));
}

/**
 * Bumped when the tab moves to another project (resetTaskStore): an answer to a request sent for
 * the last project is dropped, so its tasks never land in this one's boards.
 */
let generation = 0;

const NO_SEARCH: BoardEntry['search'] = { ids: [], text: null, error: null, more: false, seq: 0 };
let searchSeq = 0;

/** The search's cards that are still only the search's: in a Done column (one moved out is an open task, the board's own). */
function searchOnlyIds(entry: BoardEntry | undefined): Set<string> {
  if (!entry?.data) return new Set();
  const done = new Set(entry.data.columns.filter((c) => c.is_done).map((c) => c.id));
  return new Set(entry.data.tasks.filter((t) => entry.search.ids.includes(t.id) && done.has(t.column_id)).map((t) => t.id));
}

/**
 * A board fetch's answer. Cards its search had added stay until the search is sent again (a full
 * fetch may follow a reconnect, so they may have changed): `text` is cleared, so it is. When the
 * answer has every Done task, it has the search's too.
 */
function withSearch(prev: BoardEntry | undefined, data: TaskBoard): Pick<BoardEntry, 'data' | 'search'> {
  const have = new Set(data.tasks.map((t) => t.id));
  const only = searchOnlyIds(prev);
  const carried = prev?.data && data.older_done?.remaining ? prev.data.tasks.filter((t) => only.has(t.id) && !have.has(t.id)) : [];
  return {
    data: carried.length ? { ...data, tasks: sortCards([...data.tasks, ...carried], data.columns) } : data,
    search: { ...NO_SEARCH, ids: carried.map((t) => t.id), seq: prev?.search.seq ?? 0 },
  };
}

/** Done for TASK_AUTO_ARCHIVE_DAYS: off the Board (the Worker's ARCHIVED_SQL), though it was on it when fetched. */
function agedOff(card: TaskCard, doneColumns: Set<string>, now: number): boolean {
  return doneColumns.has(card.column_id) && card.done_at !== null && Date.parse(card.done_at) < now - TASK_AUTO_ARCHIVE_DAYS * 86_400_000;
}

/**
 * The board with refetched cards in place (replaced, added, or dropped when deleted or no longer
 * on it), in the server's order, with the newest tag counts and members, as a full refetch would have it.
 * Updates older than the board's data, or than that card's last refetch, are skipped.
 */
export function mergeCards(entry: BoardEntry, updates: CardUpdate[]): BoardEntry {
  if (!entry.data) return entry;
  const fresh = updates.filter((u) => u.at > entry.fetchedFrom && u.at > (entry.cardsFetchedFrom[u.id] ?? 0));
  if (!fresh.length) return entry;
  const changed = new Set(fresh.map((u) => u.id));
  const doneColumns = new Set(entry.data.columns.filter((c) => c.is_done).map((c) => c.id));
  const now = Date.now();
  const tasks = sortCards(
    [
      ...entry.data.tasks.filter((t) => !changed.has(t.id) && (entry.query.archived || !agedOff(t, doneColumns, now))),
      ...fresh.flatMap((u) => (u.card && belongsOn(entry.query, u.card) ? [u.card] : [])),
    ],
    entry.data.columns
  );
  const cardsFetchedFrom = { ...entry.cardsFetchedFrom };
  for (const u of fresh) cardsFetchedFrom[u.id] = u.at;
  const meta = freshMeta && freshMeta.at > entry.fetchedFrom ? { tags: freshMeta.tags, members: freshMeta.members } : {};
  return { ...entry, data: { ...entry.data, tasks, ...meta }, cardsFetchedFrom };
}

/** A search's finds in place of the last one's (cards the board has anyway aren't the search's). */
function withSearchResults(entry: BoardEntry, text: string, page: TaskFinishedPage, at: number): BoardEntry {
  if (!entry.data) return entry;
  const found = new Set(page.tasks.map((c) => c.id));
  const only = searchOnlyIds(entry);
  const dropped = new Set([...only].filter((id) => !found.has(id)));
  const own = new Set(entry.data.tasks.filter((t) => !only.has(t.id)).map((t) => t.id));
  const base: BoardEntry = { ...entry, data: { ...entry.data, tasks: entry.data.tasks.filter((t) => !dropped.has(t.id)) } };
  const merged = mergeCards(base, page.tasks.map((card) => ({ id: card.id, card, at })));
  const ids = page.tasks.filter((c) => !own.has(c.id) && merged.data?.tasks.some((t) => t.id === c.id)).map((c) => c.id);
  return { ...merged, search: { ids, text, error: null, more: page.more, seq: entry.search.seq } };
}

function applyCardUpdates(updates: CardUpdate[], meta?: BoardMeta): void {
  const now = Date.now();
  // A board fetch still on its way needs every card refetched since it started.
  const oldestFetch = Math.min(...[...inFlight.values()].map((f) => f.startedAt));
  for (const [id, u] of freshCards) if (now - u.at > FRESH_CARD_MS && u.at < oldestFetch) freshCards.delete(id);
  for (const u of updates) if ((freshCards.get(u.id)?.at ?? 0) < u.at) freshCards.set(u.id, u);
  if (meta && (freshMeta?.at ?? 0) < meta.at) freshMeta = meta;
  useTaskStore.setState((s) => ({
    boards: Object.fromEntries(Object.entries(s.boards).map(([key, entry]) => [key, mergeCards(entry, updates)])),
  }));
}

/** Refetch the cards of the tasks that changed, into every loaded board. */
function refreshCards(): void {
  cardTimer = null;
  const ids = [...staleTaskIds];
  staleTaskIds.clear();
  // Nothing loaded or loading: the next board fetch has them anyway.
  if (!ids.length || !Object.values(useTaskStore.getState().boards).some((e) => e.data || e.loading)) return;
  const at = Date.now();
  const gen = generation;
  // The tab's project, which the requests name: their tag counts and people go into its cached meta.
  const projectId = getCurrentProject()?.id;
  for (let i = 0; i < ids.length; i += CARDS_PER_REQUEST) {
    const chunk = ids.slice(i, i + CARDS_PER_REQUEST);
    void getTaskCards(chunk).then((res) => {
      if (gen !== generation) return;
      if (!res.data) {
        // Couldn't get the cards: fall back to fetching the boards again.
        useTaskStore.setState((s) => ({ boardTick: s.boardTick + 1 }));
        return;
      }
      const byId = new Map(res.data.tasks.map((t) => [t.id, t]));
      // The cards asked for (null: deleted), plus the cards Waiting on them that came along.
      const updates: CardUpdate[] = [
        ...chunk.map((id) => ({ id, card: byId.get(id) ?? null, at })),
        ...res.data.tasks.filter((t) => !chunk.includes(t.id)).map((card) => ({ id: card.id, card, at })),
      ];
      applyCardUpdates(updates, { tags: res.data.tags, members: res.data.members, at });
      if (projectId) rememberLiveMeta(projectId, res.data);
    });
  }
}

function queueCardRefresh(taskId: string): void {
  staleTaskIds.add(taskId);
  if (!cardTimer) cardTimer = setTimeout(refreshCards, CARD_REFRESH_MS);
}

export const useTaskStore = create<TaskState>()((set, get) => ({
  boards: {},
  panel: { open: false, focus: null, taskId: null },
  setPanel: (panel) => set((s) => ({ panel: { ...s.panel, ...panel } })),
  boardTick: 0,
  taskTicks: {},
  prTicks: {},
  prsTick: 0,
  githubTick: 0,
  devServersTicks: {},
  devServersTick: 0,
  prMergeBatches: {},
  // A read that left before a live step and landed after it is older: the batch only moves forward.
  setPrMergeBatch: (batch) =>
    set((s) => {
      const known = s.prMergeBatches[batch.id];
      return known && batchProgress(known) > batchProgress(batch) ? {} : { prMergeBatches: { ...s.prMergeBatches, [batch.id]: batch } };
    }),
  resyncTick: 0,
  notificationTick: 0,
  connected: false,
  disconnectedAt: null,

  loadBoard: (query) => {
    const key = boardKey(query);
    const pending = inFlight.get(key);
    const done = get().boards[key]?.doneLimit ?? TASK_BOARD_DONE_LIMIT;
    // Joins at the same tick share one fetch; a live change since the request started may be
    // missing from its answer (or Show older wants more), so it gets one more fetch.
    if (pending) {
      return pending.tick === get().boardTick && pending.done === done
        ? pending.request
        : pending.request.then(() => get().loadBoard(query));
    }
    set((s) => ({
      boards: {
        ...s.boards,
        [key]: {
          query,
          data: s.boards[key]?.data ?? null,
          fetchedFrom: s.boards[key]?.fetchedFrom ?? 0,
          cardsFetchedFrom: s.boards[key]?.cardsFetchedFrom ?? {},
          doneLimit: s.boards[key]?.doneLimit ?? TASK_BOARD_DONE_LIMIT,
          loadingOlder: s.boards[key]?.loadingOlder ?? false,
          search: s.boards[key]?.search ?? NO_SEARCH,
          loading: true,
          error: null,
        },
      },
    }));
    const startedAt = Date.now();
    const gen = generation;
    // The tab's project, which the request names: its answer carries that project's task meta too.
    const projectId = getCurrentProject()?.id;
    const request: Promise<void> = getTaskBoard(query, done)
      .then((res) => {
        if (gen !== generation) return;
        if (res.data && projectId) rememberTaskMeta(projectId, res.data);
        set((s) => {
          const prev = s.boards[key];
          const entry: BoardEntry = res.data
            ? {
                query,
                ...withSearch(prev, res.data),
                fetchedFrom: startedAt,
                cardsFetchedFrom: {},
                // Show older may have asked for more while this was on its way: that fetch follows.
                doneLimit: Math.max(prev?.doneLimit ?? done, done),
                loadingOlder: (prev?.doneLimit ?? done) > done,
                loading: false,
                error: null,
              }
            : {
                query,
                data: prev?.data ?? null,
                fetchedFrom: prev?.fetchedFrom ?? 0,
                cardsFetchedFrom: prev?.cardsFetchedFrom ?? {},
                doneLimit: prev?.doneLimit ?? TASK_BOARD_DONE_LIMIT,
                loadingOlder: false,
                search: prev?.search ?? NO_SEARCH,
                loading: false,
                error: res.error ?? null,
              };
          // Cards refetched while this was on its way are newer than it.
          return { boards: { ...s.boards, [key]: res.data ? mergeCards(entry, [...freshCards.values()]) : entry } };
        });
      })
      .finally(() => {
        if (inFlight.get(key)?.request === request) inFlight.delete(key);
      });
    inFlight.set(key, { request, tick: get().boardTick, startedAt, done });
    return request;
  },

  loadOlderDone: async (query) => {
    const key = boardKey(query);
    const entry = get().boards[key];
    const limit = Math.min((entry?.doneLimit ?? TASK_BOARD_DONE_LIMIT) + TASK_BOARD_DONE_LIMIT, TASK_BOARD_DONE_MAX);
    if (!entry?.data?.older_done?.remaining || entry.loadingOlder || limit === entry.doneLimit) return;
    // The board again with more of them: what it shows is always one answer from the server.
    set((s) => ({ boards: { ...s.boards, [key]: { ...s.boards[key], doneLimit: limit, loadingOlder: true } } }));
    await get().loadBoard(query);
    if (get().boards[key]?.error) {
      // Back to what it had, so later fetches don't keep asking for what failed.
      set((s) => ({ boards: { ...s.boards, [key]: { ...s.boards[key], doneLimit: entry.doneLimit, loadingOlder: false } } }));
      toast.error("Couldn't load older tasks");
    }
  },

  searchFinished: async (query, q) => {
    const key = boardKey(query);
    const text = q.trim();
    const entry = get().boards[key];
    if (!entry?.data) return;
    const seq = ++searchSeq;
    if (!text) {
      // Search cleared: its cards go (not ones moved out of Done since), and any answer on its way is dropped.
      const only = searchOnlyIds(entry);
      set((s) => ({
        boards: {
          ...s.boards,
          [key]: { ...entry, data: { ...entry.data!, tasks: entry.data!.tasks.filter((t) => !only.has(t.id)) }, search: { ...NO_SEARCH, seq } },
        },
      }));
      return;
    }
    // Nothing older to find, or already found for this text since the last full fetch.
    if (!entry.data.older_done?.remaining || entry.search.text?.toLowerCase() === text.toLowerCase()) return;
    set((s) => ({ boards: { ...s.boards, [key]: { ...s.boards[key], search: { ...s.boards[key].search, seq } } } }));
    const at = Date.now();
    const gen = generation;
    const res = await getFinishedTasks({ archived: query.archived, orphaned: query.orphaned, q: text });
    if (gen !== generation) return;
    set((s) => {
      const current = s.boards[key];
      // A newer search (or clearing it) came since, or a full fetch landed meanwhile and the search goes again for it.
      if (!current?.data || current.search.seq !== seq || at <= current.fetchedFrom) return {};
      const next = res.data
        ? withSearchResults(current, text, res.data, at)
        : { ...current, search: { ...current.search, text: null, error: res.error ?? "Couldn't search older tasks" } };
      return { boards: { ...s.boards, [key]: next } };
    });
  },

  receive: (message) => {
    // Who's editing (T-46) is taskEditingStore's; it changes nothing here.
    if (message.type === 'editors') return;
    if (message.type === 'notification') {
      set((s) => ({ notificationTick: s.notificationTick + 1 }));
      return;
    }
    if (message.type === 'github_activity') {
      set((s) => ({ githubTick: s.githubTick + 1 }));
      return;
    }
    if (message.type === 'dev_servers') {
      const taskId = message.task_id;
      set((s) => ({ devServersTick: s.devServersTick + 1, devServersTicks: { ...s.devServersTicks, [taskId]: (s.devServersTicks[taskId] ?? 0) + 1 } }));
      return;
    }
    if (message.type === 'pr_merge_batch') {
      get().setPrMergeBatch(message.batch);
      return;
    }
    if (message.type === 'task_prs') {
      const taskId = message.task_id;
      set((s) => ({ prTicks: { ...s.prTicks, [taskId]: (s.prTicks[taskId] ?? 0) + 1 } }));
      return;
    }
    if (message.type === 'task' || message.type === 'task_deleted') {
      const taskId = message.task_id;
      set((s) => ({ taskTicks: { ...s.taskTicks, [taskId]: (s.taskTicks[taskId] ?? 0) + 1 } }));
      // A deleted task leaves at once; the refetch still brings the tag counts without its tags.
      if (message.type === 'task_deleted') applyCardUpdates([{ id: taskId, card: null, at: Date.now() }]);
      queueCardRefresh(taskId);
      return;
    }
    set((s) => ({ boardTick: s.boardTick + 1 }));
  },

  /** After a reconnect: anything may have changed while we were away. */
  resync: () =>
    set((s) => ({
      boardTick: s.boardTick + 1,
      notificationTick: s.notificationTick + 1,
      prsTick: s.prsTick + 1,
      githubTick: s.githubTick + 1,
      devServersTick: s.devServersTick + 1,
      resyncTick: s.resyncTick + 1,
      taskTicks: Object.fromEntries(Object.entries(s.taskTicks).map(([id, n]) => [id, n + 1])),
    })),

  setConnected: (connected, { idle = false } = {}) =>
    set((s) => ({
      connected,
      // Keeps the first moment of an outage across reconnect attempts; idle (no socket wanted) has none.
      disconnectedAt: connected || idle ? null : (s.disconnectedAt ?? Date.now()),
    })),
}));

/**
 * Forget every board, tick and panel: the tab moved to another project (or left projects). Answers
 * still on their way for the last project are dropped (`generation`).
 */
export function resetTaskStore(): void {
  generation += 1;
  inFlight.clear();
  staleTaskIds.clear();
  if (cardTimer) clearTimeout(cardTimer);
  cardTimer = null;
  freshCards.clear();
  freshMeta = null;
  useTaskStore.setState({
    boards: {},
    panel: { open: false, focus: null, taskId: null },
    taskTicks: {},
    prTicks: {},
    devServersTicks: {},
    // SF-T-198: another project's batches (their titles and PRs) never show on this one's Board.
    prMergeBatches: {},
    devServersTick: useTaskStore.getState().devServersTick + 1,
    prsTick: useTaskStore.getState().prsTick + 1,
    // Ticks only move forward, so hooks that remember the last one still see a change.
    boardTick: useTaskStore.getState().boardTick + 1,
    notificationTick: useTaskStore.getState().notificationTick + 1,
  });
}

onProjectChange(() => resetTaskStore());

// Confirmed optimistic changes refetch the task (and, through the tick, the boards). Label
// changes have no task, so they just refresh the boards.
setOptimisticRefetch((taskId) =>
  useTaskStore
    .getState()
    .receive(taskId ? { type: 'task', task_id: taskId, actor_user_id: null } : { type: 'board', actor_user_id: null })
);

/** The bottom rank of a column across loaded boards, so an optimistic card lands where the server will put it. */
export function lastRankIn(columnId: string, excludeTaskId?: string): string | null {
  let last: string | null = null;
  for (const entry of Object.values(useTaskStore.getState().boards)) {
    for (const t of entry.data?.tasks ?? []) {
      if (t.column_id === columnId && t.id !== excludeTaskId && (last === null || t.rank > last)) last = t.rank;
    }
  }
  return last;
}
